import { useState } from "react";
import { useParams, useLocation } from "react-router";
import { useOutletContext } from "react-router-dom";
import { Paper, Stack, Typography, Grid } from "@mui/material";

import RetakePhotoPopUp from "../../components/PopUp/RetakePhoto";
import Loader from "../../components/Loader";
import useGetPhotoSessionView from "../../hooks/useGetPhotoSessionView";
import useRetakePhotoMutation from "../../hooks/useRetakePhotoMutation";
import ImageCard from "../../components/images/ImageCard";
import CompareDialog from "../../components/images/CompareDialog";
import ZoomDialog from "../../components/images/ZoomDialog";
import EcrfBlocks from "../../components/Trials/EcrfBlocks";
import { INV_RELATED_ROLES, USER_ROLES } from "../../utils/constants";

const PatientCase = () => {
  const { _id_, caseId } = useParams();
  const location = useLocation();

  const userData = JSON.parse(localStorage.getItem("user_data"));
  const role = userData?.role;

  const visitName =
    location.state?.visitName || new URLSearchParams(location.search).get("visitName");

  const { setActive, setBreadcrumbTitle, isTrialLocked } = useOutletContext();
  setActive("photos");
  setBreadcrumbTitle("Photos - Case View");

  const [modalRetake, setModalRetake] = useState(false);
  const [photoId, setPhotoId] = useState(null);

  // Compare dialog
  const [compareOpen, setCompareOpen] = useState(false);
  const [selectedCompareImage, setSelectedCompareImage] = useState(null);

  // Zoom dialog
  const [zoomImage, setZoomImage] = useState(null);

  // Score eCRF dialog
  const [scoreDialogOpen, setScoreDialogOpen] = useState(false);

  const { data: PatientData, isLoading } = useGetPhotoSessionView({
    patientId: _id_,
    sessionId: caseId,
  });

  const retakePhotoMutation = useRetakePhotoMutation();

  const canScore =
    !isTrialLocked && (INV_RELATED_ROLES.includes(role) || role === USER_ROLES.REVIEWER);

  const handleOnImageClick = (img) => {
    setCompareOpen(true);
    setSelectedCompareImage(img);
  };

  const handleRetakePhoto = () => {
    retakePhotoMutation.mutate(
      { photoId },
      {
        onSuccess: () => {
          setModalRetake(false);
          setPhotoId(null);
        },
      }
    );
  };

  return (
    <>
      <Paper elevation={1} sx={{ p: 2 }}>
        {/* Header */}
        <Typography variant="h6" mb={3}>
          Visit Name: {visitName}
        </Typography>

        {/* Loader */}
        {isLoading && (
          <Stack alignItems="center" py={4}>
            <Loader />
          </Stack>
        )}

        {/* Photos Grid */}
        <Grid container spacing={2}>
          {PatientData?.photo?.map((photo) => (
            <>
              <Grid item xs={12} sm={6} md={3} key={photo.id}>
                <ImageCard
                  photo={photo}
                  onCompare={() => handleOnImageClick(photo.image)}
                  onZoom={() => setZoomImage(photo.image)}
                  onRetake={() => {
                    setModalRetake(true);
                    setPhotoId(photo.id);
                  }}
                  showRetakeButton={true}
                  canRetake={
                    !isTrialLocked &&
                    (role === "Investigator" ||
                      role === "Inv_coordinator" ||
                      role === "Reviewer") &&
                    !PatientData?.has_next_visit_started
                  }
                  showScoreButton={Boolean(PatientData?.score_ecrf_name)}
                  canScore={canScore}
                  onScore={() => setScoreDialogOpen(true)}
                />
              </Grid>
            </>
          ))}
        </Grid>
      </Paper>

      {/* SIDE-BY-SIDE COMPARE DIALOG */}
      <CompareDialog
        open={compareOpen}
        images={PatientData?.photo || []}
        onClose={() => setCompareOpen(false)}
        selectedImage={selectedCompareImage}
      />

      {/* ZOOM DIALOG */}
      <ZoomDialog
        open={!!zoomImage}
        onClose={() => {
          setZoomImage(null);
        }}
        image={zoomImage}
      />

      {modalRetake && (
        <RetakePhotoPopUp setModalRetake={setModalRetake} handleRetakePhoto={handleRetakePhoto} />
      )}

      {scoreDialogOpen && (
        <EcrfBlocks
          visit_number={PatientData?.visit_number}
          visitName={PatientData?.visit_name || visitName}
          trialId={PatientData?.trial_id}
          trialName={PatientData?.trial_name}
          selectedBlock={PatientData?.score_ecrf_name}
          fromQueryTable={true}
          useCompactModal={true}
          showEcrfBlock={scoreDialogOpen}
          setShowEcrfBlock={setScoreDialogOpen}
          patientId={_id_}
          isTrialLocked={isTrialLocked}
          selectedSessionId={caseId}
        />
      )}
    </>
  );
};

export default PatientCase;
